// Settings and users (SCR-FND-04, SCR-FND-05): reads from api_v1 views, writes through operation functions.
import type { Role } from '../app/pages';
import type { Language } from '../i18n';
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { functionsUrl, supabase } from './supabase';

export interface Settings {
  business_name: string;
  language: Language;
  country: string;
  currency: string;
  time_zone: string;
  business_start_date: string;
  return_days: number;
  due_days: number;
  expiry_alert_days: number;
  min_app_version: string;
}
export interface UserRow {
  user_id: string;
  display_name: string;
  email: string;
  role: Role;
  active: boolean;
}
export interface AuditRow {
  id: number;
  at: string;
  business_date: string;
  action: string;
  actor_name: string | null;
}
export type PolicyKey = 'return_days' | 'due_days' | 'expiry_alert_days';

function check<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Refusal('SERVER_ERROR');
  if (res.data === null) throw new Refusal('NOT_SET_UP');
  return res.data;
}

export async function loadSettings(): Promise<Settings> {
  return check(await supabase.from('v_settings').select('*').maybeSingle()) as Settings;
}
export async function loadUsers(): Promise<UserRow[]> {
  return check(
    await supabase.from('v_users').select('user_id, display_name, email, role, active').order('display_name'),
  ) as UserRow[];
}
export async function loadAudit(): Promise<AuditRow[]> {
  return check(
    await supabase
      .from('v_audit')
      .select('id, at, business_date, action, actor_name')
      .order('id', { ascending: false })
      .limit(30),
  ) as AuditRow[];
}

export const updatePolicy = (key: PolicyKey, value: number) =>
  sendCommand('update_policy', { p_key: key, p_value: value });
export const setBusinessLanguage = (language: Language) =>
  sendCommand('set_business_language', { p_language: language });
export const updateUserRole = (user: string, role: Role) =>
  sendCommand('update_user_role', { p_user: user, p_role: role });
export const setUserActive = (user: string, active: boolean) =>
  sendCommand('set_user_active', { p_user: user, p_active: active });

export interface CreatedUser {
  userId: string;
  email: string;
  temporaryPassword: string;
}
/** Owner only (ADR-086): the create-user Edge Function creates the account; the password is shown once. */
export async function createUser(input: { name: string; email: string; role: Role }): Promise<CreatedUser> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Refusal('NOT_SIGNED_IN');
  let res: Response;
  try {
    res = await fetch(`${functionsUrl}/create-user`, {
      method: 'POST',
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });
  } catch {
    throw new Refusal('OFFLINE');
  }
  const body = (await res.json().catch(() => ({}))) as Partial<CreatedUser> & { error?: string };
  if (!res.ok || body.error || !body.temporaryPassword) throw new Refusal(body.error ?? 'SERVER_ERROR');
  return body as CreatedUser;
}
